import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { useAuth } from "../../../contexts/AuthContext";
import { toast } from "sonner";
import { Home, LogOut, User, Lock, Globe, Mail, Phone, Settings, Coins } from "lucide-react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { LanguageSwitcher } from "../../../components/LanguageSwitcher";
import { CurrencySwitcher } from "../../../components/currency/CurrencySwitcher";

const AdminSettings = () => {
    const navigate = useNavigate();
    const { user, logout, updateUser, changePassword } = useAuth();
    const { language, t } = useLanguage();
    const [activeSection, setActiveSection] = useState("personal");

    const [personalInfo, setPersonalInfo] = useState({
        name: user?.name || "",
        email: user?.email || "",
        phone: user?.phone || "",
    });

    const [passwordInfo, setPasswordInfo] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    const handlePersonalInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPersonalInfo({
            ...personalInfo,
            [e.target.name]: e.target.value,
        });
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPasswordInfo({
            ...passwordInfo,
            [e.target.name]: e.target.value,
        });
    };

    const handleSavePersonalInfo = async (e: React.FormEvent) => {
        e.preventDefault();
        const success = await updateUser(personalInfo);
        if (success) {
            toast.success(t("settings.personalInfo.success") || "Personal information updated successfully");
        } else {
            toast.error(t("settings.personalInfo.error") || "Failed to update personal information");
        }
    };

    const handleSavePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (passwordInfo.newPassword !== passwordInfo.confirmPassword) {
            toast.error(t("auth.register.passwordMismatch") || "Passwords do not match");
            return;
        }
        const success = await changePassword(passwordInfo.currentPassword, passwordInfo.newPassword);
        if (success) {
            toast.success(t("settings.password.success") || "Password updated successfully");
            setPasswordInfo({
                currentPassword: "",
                newPassword: "",
                confirmPassword: "",
            });
        } else {
            toast.error(t("settings.password.error") || "Failed to update password. Please check your current password.");
        }
    };

    return (
        <div className="min-h-screen bg-background" dir={language === "ar" ? "rtl" : "ltr"}>
            <aside className="fixed right-0 top-0 h-full w-64 bg-card border-l border-border p-4 hidden lg:block" dir={language === "ar" ? "rtl" : "ltr"}>
                <div className="h-20 mb-6 flex items-center justify-center border-b border-border">
                    <h2 className="text-xl font-bold text-primary">{t("auth.register.admin")}</h2>
                </div>

                <nav className="space-y-2">
                    <Button
                        variant={activeSection === "personal" ? "default" : "ghost"}
                        className="w-full justify-start gap-3"
                        onClick={() => setActiveSection("personal")}
                    >
                        <User className="w-4 h-4" />
                        {t("settings.personalInfo.title")}
                    </Button>
                    <Button
                        variant={activeSection === "password" ? "default" : "ghost"}
                        className="w-full justify-start gap-3"
                        onClick={() => setActiveSection("password")}
                    >
                        <Lock className="w-4 h-4" />
                        {t("settings.password.title")}
                    </Button>
                    <Button
                        variant={activeSection === "language" ? "default" : "ghost"}
                        className="w-full justify-start gap-3"
                        onClick={() => setActiveSection("language")}
                    >
                        <Globe className="w-4 h-4" />
                        {t("settings.language.title")}
                    </Button>
                    <Button
                        variant={activeSection === "currency" ? "default" : "ghost"}
                        className="w-full justify-start gap-3"
                        onClick={() => setActiveSection("currency")}
                    >
                        <Coins className="w-4 h-4" />
                        {t("settings.currency.title")}
                    </Button>
                </nav>

                <div className="absolute bottom-4 right-4 left-4 space-y-2">
                    <Link to="/admin">
                        <Button variant="outline" className="w-full justify-start gap-3">
                            <Home className="w-4 h-4" />
                            {t("dashboard.admin.title")}
                        </Button>
                    </Link>
                    <Button variant="ghost" className="w-full justify-start gap-3 text-destructive hover:text-destructive" onClick={handleLogout}>
                        <LogOut className="w-4 h-4" />
                        {t("common.logout")}
                    </Button>
                </div>
            </aside>

            <main className="lg:mr-64 p-6" dir={language === "ar" ? "rtl" : "ltr"}>
                <div className="max-w-4xl mx-auto space-y-6">
                    <div className="flex items-center justify-between mb-8 w-full">
                        <div>
                            <h1 className="text-2xl font-bold text-foreground">{t("settings.title")}</h1>
                            <p className="text-muted-foreground">{t("settings.subtitle")}</p>
                        </div>
                        <img
                            src="/logo.png"
                            alt="Knowvest.plus"
                            className="h-16 w-auto object-contain"
                        />
                    </div>

                    {activeSection === "personal" && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <User className="w-5 h-5" />
                                    {t("settings.personalInfo.title")}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSavePersonalInfo} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="name">{t("settings.personalInfo.fullName")}</Label>
                                            <div className="relative">
                                                <User className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                <Input
                                                    id="name"
                                                    name="name"
                                                    value={personalInfo.name}
                                                    onChange={handlePersonalInfoChange}
                                                    className="pr-10"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <Label htmlFor="email">{t("settings.personalInfo.email")}</Label>
                                            <div className="relative">
                                                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                <Input
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    value={personalInfo.email}
                                                    onChange={handlePersonalInfoChange}
                                                    className="pr-10"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <Label htmlFor="phone">{t("settings.personalInfo.phone")}</Label>
                                            <div className="relative">
                                                <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                                <Input
                                                    id="phone"
                                                    name="phone"
                                                    value={personalInfo.phone}
                                                    onChange={handlePersonalInfoChange}
                                                    className="pr-10"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex justify-end">
                                        <Button type="submit">{t("settings.personalInfo.save")}</Button>
                                    </div>
                                </form>
                            </CardContent>
                        </Card>
                    )}

                    {activeSection === "password" && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Lock className="w-5 h-5" />
                                    {t("settings.password.title")}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleSavePassword} className="space-y-4">
                                    <div>
                                        <Label htmlFor="currentPassword">{t("settings.password.current")}</Label>
                                        <Input
                                            id="currentPassword"
                                            name="currentPassword"
                                            type="password"
                                            value={passwordInfo.currentPassword}
                                            onChange={handlePasswordChange}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="newPassword">{t("settings.password.new")}</Label>
                                        <Input
                                            id="newPassword"
                                            name="newPassword"
                                            type="password"
                                            value={passwordInfo.newPassword}
                                            onChange={handlePasswordChange}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="confirmPassword">{t("settings.password.confirm")}</Label>
                                        <Input
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            type="password"
                                            value={passwordInfo.confirmPassword}
                                            onChange={handlePasswordChange}
                                        />
                                    </div>
                                    <div className="flex justify-end">
                                        <Button type="submit">{t("settings.password.update")}</Button>
                                    </div>
                                </form>
                            </CardContent>
                        </Card>
                    )}

                    {activeSection === "language" && (
                        <LanguageSwitcher />
                    )}
                    {activeSection === "currency" && (
                        <CurrencySwitcher />
                    )}
                </div>
            </main>
        </div>
    );
};

export default AdminSettings;
